// The admin-gated `/admin/access` users table. Mirrors AnalyticsPage: a
// `.loom-dashboard`-scoped shell, a server-owned forbidden state (rendered off a
// 403, not a client-side role check), and tables. Each row exposes the account's
// approval status (with a control to change it) and an expandable list of
// per-project grants with remove + add-grant affordances.
//
// All mutations go through useAccess, then `reload()` the authoritative list —
// the table never patches its own state optimistically.

import { useCallback, useEffect, useMemo, useState, type JSX } from "react";

import type {
  AccessLevelWire,
  AccessStatusWire,
  AdminGrant,
  AdminUserRow,
} from "@loom/shared/admin";

import { useT, type Translator } from "../i18n/index.js";
import { fetchProjects, type Project } from "../projects/useProjects";

import { useAccess } from "./useAccess";

interface AccessPageProps {
  readonly onBack: () => void;
}

const STATUSES: ReadonlyArray<AccessStatusWire> = ["pending", "approved", "blocked"];
const LEVELS: ReadonlyArray<AccessLevelWire> = ["create_only", "full"];

const statusLabel = (t: Translator, status: AccessStatusWire): string =>
  status === "pending"
    ? t("access.status.pending")
    : status === "approved"
      ? t("access.status.approved")
      : t("access.status.blocked");

const levelLabel = (t: Translator, level: AccessLevelWire): string =>
  level === "full" ? t("access.level.full") : t("access.level.create_only");

export const AccessPage = ({ onBack }: AccessPageProps): JSX.Element => {
  const t = useT();
  const access = useAccess();
  const [saveError, setSaveError] = useState<string | null>(null);

  // Wrap a mutation so its error surfaces in one place and the list reloads.
  const run = useCallback(
    async (fn: () => Promise<void>): Promise<void> => {
      setSaveError(null);
      try {
        await fn();
        access.reload();
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : String(err));
      }
    },
    [access],
  );

  const shell = (body: JSX.Element): JSX.Element => (
    <div className="loom-dashboard admin-access-page">
      <header className="admin-access-head">
        <button className="admin-access-back" onClick={onBack} type="button">
          ← {t("admin.access.back")}
        </button>
        <div>
          <h1 className="admin-access-title">{t("admin.access.title")}</h1>
          <p className="admin-access-subtitle">{t("admin.access.subtitle")}</p>
        </div>
      </header>
      {body}
    </div>
  );

  if (access.forbidden) {
    return shell(
      <div className="admin-access-state admin-access-denied">
        <h2>{t("admin.access.denied.title")}</h2>
        <p>{t("admin.access.denied.body")}</p>
      </div>,
    );
  }
  if (access.error) {
    return shell(
      <div className="admin-access-state admin-access-state-error">
        <h2>{t("admin.access.error.title")}</h2>
        <p>{access.error}</p>
      </div>,
    );
  }
  if (access.loading) {
    return shell(<div className="admin-access-state">{t("admin.access.loading")}</div>);
  }

  return shell(
    <>
      {saveError ? (
        <p className="admin-access-state-error">
          {t("admin.access.saveError", { error: saveError })}
        </p>
      ) : null}
      {access.users.length ? (
        <table className="admin-access-table">
          <thead>
            <tr>
              <th>{t("admin.access.col.user")}</th>
              <th>{t("admin.access.col.status")}</th>
              <th>{t("admin.access.col.grants")}</th>
            </tr>
          </thead>
          <tbody>
            {access.users.map((user) => (
              <UserRow
                key={`${user.gitlabHost}:${user.userId}`}
                user={user}
                onSetStatus={(status) =>
                  void run(() => access.setStatus(user.userId, status))
                }
                onSetLevel={(projectId, level) =>
                  void run(() => access.setLevel(user.userId, projectId, level))
                }
                onRevoke={(projectId) =>
                  void run(() => access.revoke(user.userId, projectId))
                }
              />
            ))}
          </tbody>
        </table>
      ) : (
        <p className="admin-access-muted">{t("admin.access.empty")}</p>
      )}
    </>,
  );
};

// ---------------------------------------------------------------------------
// One user row + its expandable grants.
// ---------------------------------------------------------------------------

const UserRow = ({
  user,
  onSetStatus,
  onSetLevel,
  onRevoke,
}: {
  user: AdminUserRow;
  onSetStatus: (status: AccessStatusWire) => void;
  onSetLevel: (projectId: number, level: AccessLevelWire) => void;
  onRevoke: (projectId: number) => void;
}): JSX.Element => {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr>
        <td>
          <span className="admin-access-user">
            <span className="admin-access-user-name">
              {user.displayName ?? user.userId}
            </span>
            {user.isAdmin ? (
              <span className="admin-access-admin-badge">{t("admin.access.badge.admin")}</span>
            ) : null}
          </span>
        </td>
        <td>
          <label className="admin-access-status-control">
            <span className="admin-access-sr-label">{t("admin.access.status.label")}</span>
            <select
              className={`admin-access-status admin-access-status-${user.status}`}
              value={user.status}
              onChange={(e) => onSetStatus(e.target.value as AccessStatusWire)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(t, s)}
                </option>
              ))}
            </select>
          </label>
        </td>
        <td>
          <button
            className="admin-access-expand"
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "▾" : "▸"}{" "}
            {open
              ? t("admin.access.grants.collapse")
              : t("admin.access.grants.expand")}
            {user.grants.length ? ` (${user.grants.length})` : ""}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="admin-access-grants-row">
          <td colSpan={3}>
            <GrantsPanel
              grants={user.grants}
              onSetLevel={onSetLevel}
              onRevoke={onRevoke}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
};

// ---------------------------------------------------------------------------
// Grants panel — the existing grants list + an add-grant editor.
// ---------------------------------------------------------------------------

const GrantsPanel = ({
  grants,
  onSetLevel,
  onRevoke,
}: {
  grants: ReadonlyArray<AdminGrant>;
  onSetLevel: (projectId: number, level: AccessLevelWire) => void;
  onRevoke: (projectId: number) => void;
}): JSX.Element => {
  const t = useT();
  return (
    <div className="admin-access-grants">
      {grants.length ? (
        <ul className="admin-access-grant-list">
          {grants.map((g) => (
            <li key={g.projectId} className="admin-access-grant">
              <span className="admin-access-grant-project">
                {g.projectPath ?? `#${g.projectId}`}
              </span>
              <select
                className="admin-access-grant-level"
                value={g.level}
                onChange={(e) =>
                  onSetLevel(g.projectId, e.target.value as AccessLevelWire)
                }
              >
                {LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {levelLabel(t, l)}
                  </option>
                ))}
              </select>
              <button
                className="admin-access-grant-remove"
                type="button"
                onClick={() => onRevoke(g.projectId)}
              >
                {t("admin.access.grants.remove")}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="admin-access-muted">{t("admin.access.grants.none")}</p>
      )}
      <AddGrant onAdd={onSetLevel} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Add-grant editor — a project picker (reuses GET /api/projects) + a level
// select. Calls `onAdd` (→ setLevel, a PUT that creates the grant).
// ---------------------------------------------------------------------------

const AddGrant = ({
  onAdd,
}: {
  onAdd: (projectId: number, level: AccessLevelWire) => void;
}): JSX.Element => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [selected, setSelected] = useState<Project | null>(null);
  const [level, setLevel] = useState<AccessLevelWire>("create_only");

  // Server-side narrowing, same shape as the focus/picker project search.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const q = query.trim();
    const handle = setTimeout(() => {
      fetchProjects(q === "" ? { recent: true } : { search: q })
        .then((next) => {
          if (!cancelled) setProjects(next);
        })
        .catch(() => {
          if (!cancelled) setProjects([]);
        });
    }, q === "" ? 0 : 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, query]);

  const results = useMemo(() => (projects ?? []).slice(0, 8), [projects]);

  const reset = (): void => {
    setOpen(false);
    setQuery("");
    setProjects(null);
    setSelected(null);
    setLevel("create_only");
  };

  if (!open) {
    return (
      <button
        className="admin-access-add-toggle"
        type="button"
        onClick={() => setOpen(true)}
      >
        + {t("admin.access.grants.add")}
      </button>
    );
  }

  return (
    <div className="admin-access-add">
      <label className="admin-access-add-field">
        <span>{t("admin.access.grants.project")}</span>
        <input
          className="admin-access-add-search"
          autoFocus
          placeholder={t("admin.access.grants.projectPlaceholder")}
          value={selected ? selected.name_with_namespace : query}
          onChange={(e) => {
            setSelected(null);
            setQuery(e.target.value);
          }}
        />
      </label>
      {!selected ? (
        <div className="admin-access-add-results">
          {results.length === 0 ? (
            <p className="admin-access-muted">{t("admin.access.grants.noProjects")}</p>
          ) : (
            <ul>
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="admin-access-add-result"
                    onClick={() => setSelected(p)}
                  >
                    {p.name_with_namespace}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      <label className="admin-access-add-field">
        <span>{t("admin.access.grants.level")}</span>
        <select value={level} onChange={(e) => setLevel(e.target.value as AccessLevelWire)}>
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              {levelLabel(t, l)}
            </option>
          ))}
        </select>
      </label>
      <div className="admin-access-add-actions">
        <button
          type="button"
          className="admin-access-add-save"
          disabled={!selected}
          onClick={() => {
            if (selected) {
              onAdd(selected.id, level);
              reset();
            }
          }}
        >
          {t("admin.access.grants.save")}
        </button>
        <button type="button" className="admin-access-add-cancel" onClick={reset}>
          {t("admin.access.grants.cancel")}
        </button>
      </div>
    </div>
  );
};
